import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Form, FormField, FormItem } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import Title from '@/components/ui/Title';
import { Plus, Trash2 } from 'lucide-react';
import Loader from '@/components/ui/Loader';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
// import { useFieldArray } from 'react-hook-form';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { toWebinarLng } from '@/lib/webinarLanguage';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button } from '@/components/ui/button';
import { Accordion } from '@/components/ui/accordion2';
import ColorSelector from '@/components/ui/ColorSelector';
import { cn } from '@/lib/utils';
import { getReadableTextColor } from '@/utils/colors';
import type { InteractionItemHandle } from '../interactionBatch';

const makeFormSchema = (t: TFunction) => {
  const surveyAnswerOptionSchema = z.object({
    id: z.number().optional(),
    position: z.number().int().nonnegative().optional(),
    textContent: z
      .string()
      .min(1, t('surveys.validation.answerRequired'))
      .max(300, t('surveys.validation.answerMax', { max: 300 }))
      .refine((val) => val.trim().length > 0, {
        message: t('surveys.validation.answerRequired'),
      }),
  });

  const surveyQuestionSchema = z.object({
    id: z.number().optional(),
    textContent: z
      .string()
      .min(1, t('surveys.validation.questionRequired'))
      .max(300, t('surveys.validation.questionMax', { max: 300 }))
      .refine((val) => val.trim().length > 0, {
        message: t('surveys.validation.questionRequired'),
      }),
    type: z.enum(['SINGLE_CHOICE', 'MULTIPLE_CHOICE']),
    position: z.number().int().nonnegative().optional(),
    required: z.boolean().optional(),
    maxSelections: z.number().int().min(1).nullable().optional(),
    randomizeOptions: z.boolean().optional(),
    options: z
      .array(surveyAnswerOptionSchema)
      .min(2, t('surveys.validation.minAnswers'))
      .max(6, t('surveys.validation.maxAnswers')),
  });

  return z.object({
    id: z.number().optional(),
    title: z
      .string()
      .min(1, t('surveys.validation.titleRequired'))
      .max(100, t('surveys.validation.titleMax', { max: 100 }))
      .refine((val) => val.trim().length > 0, {
        message: t('surveys.validation.titleRequired'),
      }),
    backgroundColor: z.string().optional(),
    titleColor: z.string().optional(),
    borderColor: z.string().optional(),
    accentColor: z.string().optional(),
    customBackgroundColor: z.string().optional(),
    customBorderColor: z.string().optional(),
    customAccentColor: z.string().optional(),
    question: surveyQuestionSchema,
  });
};

type SurveyFormValues = z.infer<ReturnType<typeof makeFormSchema>>;
type SurveyQuestionFormValue = SurveyFormValues['question'];
type SurveyAnswerOptionFormValue = SurveyQuestionFormValue['options'][number];

const createEmptyOption = (position: number): SurveyAnswerOptionFormValue => ({
  textContent: '',
  position,
});

const ensureMinimumOptions = (
  options: SurveyAnswerOptionFormValue[],
): SurveyAnswerOptionFormValue[] => {
  if (options.length >= 2) {
    return options;
  }

  const filledOptions = [...options];
  while (filledOptions.length < 2) {
    filledOptions.push(createEmptyOption(filledOptions.length));
  }

  return filledOptions;
};

const normalizeOptions = (
  options?: { id?: number; textContent?: string; position?: number }[],
  legacyResponses?: string[],
): SurveyAnswerOptionFormValue[] => {
  if (options?.length) {
    return ensureMinimumOptions(
      options.map((option, index) => ({
        id: option.id,
        textContent: option.textContent ?? '',
        position: option.position ?? index,
      })),
    );
  }

  if (legacyResponses?.length) {
    return ensureMinimumOptions(
      legacyResponses.map((text, index) => ({
        textContent: text,
        position: index,
      })),
    );
  }

  return ensureMinimumOptions([createEmptyOption(0), createEmptyOption(1)]);
};

const mapToQuestionType = (type?: string): SurveyQuestionFormValue['type'] =>
  type === 'MULTIPLE_CHOICE' || type === 'multiple' ? 'MULTIPLE_CHOICE' : 'SINGLE_CHOICE';

const createDefaultQuestion = (): SurveyQuestionFormValue => ({
  textContent: '',
  type: 'SINGLE_CHOICE',
  position: 0,
  required: false,
  randomizeOptions: false,
  maxSelections: null,
  options: [createEmptyOption(0), createEmptyOption(1)],
});

const buildInitialQuestion = (existingSurvey?: any): SurveyQuestionFormValue => {
  if (!existingSurvey) {
    return createDefaultQuestion();
  }

  const sourceQuestion = existingSurvey.questions?.[0];
  return {
    ...createDefaultQuestion(),
    id: sourceQuestion?.id,
    textContent: sourceQuestion?.textContent ?? existingSurvey.question ?? '',
    type: mapToQuestionType(sourceQuestion?.type ?? existingSurvey.type),
    position: sourceQuestion?.position ?? 0,
    required: sourceQuestion?.required ?? false,
    randomizeOptions: sourceQuestion?.randomizeOptions ?? false,
    maxSelections: sourceQuestion?.maxSelections ?? existingSurvey.maxSelections ?? null,
    options: normalizeOptions(sourceQuestion?.options, existingSurvey.responses),
  };
};

interface SurveyFormProps {
  surveyIndex: number;
  // surveyId: string;
  onRemove: () => void;
  onRemoveAnswerOption: (answerOptionId: number) => void;
  onSubmit: (data: SurveyFormValues) => void;
  existingSurvey?: any;
  canRemove: boolean;
  isModal?: boolean;
  onSave?: () => void;
  onDelete?: () => void;
  isSubmitting?: boolean;
  isEvergreen?: boolean;
  readOnly?: boolean;
  // Mode sauvegarde globale : masque le bouton « Enregistrer » par élément.
  batchMode?: boolean;
  // Remonte l'état « modifications non enregistrées » au groupe parent.
  onDirtyChange?: (dirty: boolean) => void;
}

const SurveyForm = forwardRef<InteractionItemHandle, SurveyFormProps>(function SurveyForm(
  {
    surveyIndex,
    // surveyId,
    onRemove,
    onRemoveAnswerOption,
    onSubmit,
    existingSurvey,
    canRemove,
    isModal,
    onSave,
    onDelete,
    isSubmitting = false,
    isEvergreen,
    readOnly,
    batchMode = false,
    onDirtyChange,
  },
  ref,
) {
  const locked = readOnly === true;
  const visualIdentity = useAppSelector(
    (state: RootState) => state.workspace.currentWorkspace?.visualIdentity,
  );
  const { t } = useTranslation('createWebinar');
  const webinarLanguage = useAppSelector((s: RootState) => s.webinar.currentWebinar?.language);
  const { t: tPreview } = useTranslation('webinars', { lng: toWebinarLng(webinarLanguage) });
  const [selectedRadioIndex, setSelectedRadioIndex] = useState<number>(0);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<Set<number>>(new Set([0]));

  const isCustomColor = (color: string | undefined): boolean => {
    if (!color) return false;
    return /^#[0-9A-Fa-f]{6}$/.test(color);
  };

  const mapStoredColorToPresetId = (color: string | null | undefined): string | undefined => {
    if (!color) return undefined;

    if (color === 'gradient1') return 'gradient1-color1';
    if (color === 'gradient2') return 'gradient2-color1';

    if (color === visualIdentity?.accentColor) return 'accent';
    if (color === visualIdentity?.backgroundColor) return 'background';
    if (color === visualIdentity?.contrastColor) return 'contrast';
    if (color === visualIdentity?.secondaryBackgroundColor) return 'secondary-background';
    if (color === visualIdentity?.gradient1Color1) return 'gradient1-color1';
    if (color === visualIdentity?.gradient2Color1) return 'gradient2-color1';

    if (color === '#000000') return 'black';
    if (color === '#FFFFFF') return 'white';

    return undefined;
  };

  const surveyData = existingSurvey ?? {};
  const initialQuestion = buildInitialQuestion(surveyData);

  const formSchema = useMemo(() => makeFormSchema(t), [t]);
  const form = useForm<SurveyFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: surveyData.id,
      title: surveyData.title || '',
      backgroundColor:
        mapStoredColorToPresetId(surveyData.backgroundColor) ||
        (isCustomColor(surveyData.backgroundColor)
          ? 'custom'
          : surveyData.backgroundColor || 'accent'),
      borderColor: isCustomColor(surveyData.borderColor)
        ? 'custom'
        : surveyData.borderColor || 'no-border',
      titleColor: surveyData.titleColor || '#000000',
      accentColor:
        mapStoredColorToPresetId(surveyData.accentColor) ||
        (isCustomColor(surveyData.accentColor) ? 'custom' : surveyData.accentColor || 'contrast'),
      customBackgroundColor:
        mapStoredColorToPresetId(surveyData.backgroundColor) ||
        !isCustomColor(surveyData.backgroundColor)
          ? surveyData.customBackgroundColor || '#000000'
          : surveyData.backgroundColor,
      customBorderColor: isCustomColor(surveyData.borderColor)
        ? surveyData.borderColor
        : surveyData.customBorderColor || '#000000',
      customAccentColor:
        mapStoredColorToPresetId(surveyData.accentColor) || !isCustomColor(surveyData.accentColor)
          ? surveyData.customAccentColor || '#000000'
          : surveyData.accentColor,
      question: initialQuestion,
    },
    mode: 'onChange',
  });

  const isDirty = form.formState.isDirty;

  useImperativeHandle(
    ref,
    () => ({
      isDirty: () => form.formState.isDirty,
      validate: () => form.trigger(),
      getValues: () => form.getValues(),
      markPristine: () => form.reset(form.getValues()),
    }),
    [form],
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // // Workaround: destructure from form.formState instead of directly from form
  // const { isValid, isDirty, isSubmitting } = form.formState;
  // const { watch, trigger } = form;

  const addAnswer = () => {
    if (locked) return;
    const currentOptions = form.getValues('question.options') || [];
    if (currentOptions.length < 6) {
      form.setValue('question.options', [
        ...currentOptions,
        createEmptyOption(currentOptions.length),
      ]);
    }
  };

  const removeAnswer = (answerIndex: number) => {
    if (locked) return;
    const currentOptions = form.getValues('question.options') || [];
    if (currentOptions.length > 2) {
      const newOptions = currentOptions
        .filter((_: SurveyAnswerOptionFormValue, index: number) => index !== answerIndex)
        .map((option: SurveyAnswerOptionFormValue, index: number) => ({
          ...option,
          position: index,
        }));
      // Suppression back-end uniquement si l'option existe déjà en base
      // (une option non encore enregistrée n'a pas d'id à supprimer).
      const optionId = currentOptions[answerIndex].id;
      if (optionId != null) {
        onRemoveAnswerOption(optionId);
      }
      form.setValue('question.options', newOptions);
    }
  };

  const colorsArray = [
    {
      id: 'accent',
      name: t('surveys.colors.accent'),
      value: visualIdentity?.accentColor,
      color: visualIdentity?.accentColor,
    },
    {
      id: 'background',
      name: t('surveys.colors.background'),
      value: visualIdentity?.backgroundColor,
      color: visualIdentity?.backgroundColor,
    },
    {
      id: 'contrast',
      name: t('surveys.colors.contrast'),
      value: visualIdentity?.contrastColor,
      color: visualIdentity?.contrastColor,
    },
    {
      id: 'gradient1-color1',
      name: t('surveys.colors.gradient1'),
      value: visualIdentity?.gradient1Color1,
      color: visualIdentity?.gradient1Color1,
    },

    {
      id: 'gradient2-color1',
      name: t('surveys.colors.gradient2'),
      value: visualIdentity?.gradient2Color1,
      color: visualIdentity?.gradient2Color1,
    },

    {
      id: 'secondary-background',
      name: t('surveys.colors.secondaryBackground'),
      value: visualIdentity?.secondaryBackgroundColor,
      color: visualIdentity?.secondaryBackgroundColor,
    },
    {
      id: 'black',
      name: t('surveys.colors.black'),
      value: '#000000',
      color: '#000000',
    },
    {
      id: 'white',
      name: t('surveys.colors.white'),
      value: '#FFFFFF',
      color: '#FFFFFF',
    },
  ];

  const borderColorsArray = [
    {
      id: 'no-border',
      name: t('surveys.noBorder'),
      value: 'transparent',
      color: 'transparent',
    },
    ...colorsArray,
  ];

  const arrayFontColors = [
    {
      name: t('surveys.colors.black'),
      value: '#000000',
    },
    {
      name: t('surveys.colors.white'),
      value: '#FFFFFF',
    },
  ];

  const getColorValueById = (colorId: string, customColor?: string) => {
    if (colorId === 'no-border') {
      return 'transparent';
    }
    if (colorId === 'custom') {
      return customColor || '#FFFFFF';
    }
    const color = colorsArray.find((c) => c.id === colorId);
    return color?.value || '#FFFFFF';
  };

  const getBackgroundStyle = (colorId: string, customColor?: string) => {
    if (colorId === 'gradient1-color1' || colorId === 'gradient1-color2') {
      return {
        background: `linear-gradient(to bottom, ${visualIdentity?.gradient1Color1}, ${visualIdentity?.gradient1Color2})`,
      };
    }
    if (colorId === 'gradient2-color1' || colorId === 'gradient2-color2') {
      return {
        background: `linear-gradient(226deg, ${visualIdentity?.gradient2Color1} 0.2%, ${visualIdentity?.gradient2Color2} 104.63%)`,
      };
    }
    return {
      backgroundColor: getColorValueById(colorId, customColor),
    };
  };

  const getBorderStyle = (colorId: string, customColor?: string) => {
    if (colorId === 'no-border') {
      return {
        border: 'none',
      };
    }
    return {
      borderColor: getColorValueById(colorId, customColor),
    };
  };

  const getFinalColor = (
    colorType: 'backgroundColor' | 'borderColor' | 'titleColor' | 'accentColor',
  ) => {
    const colorId = form.watch(colorType);
    const customColorField =
      `custom${colorType.charAt(0).toUpperCase() + colorType.slice(1)}` as any;
    const customColor = form.watch(customColorField);

    if (colorId === 'custom') {
      return customColor || '#000000';
    }

    return getColorValueById(colorId || 'accent', customColor);
  };

  const handleSubmit = async (data: SurveyFormValues) => {
    if (locked) return;
    await onSubmit(data);
  };

  // Check if survey is already created
  // const isSurveyCreated = !!existingSurvey?.id;

  // useEffect(() => {
  //   let timeoutId: ReturnType<typeof setTimeout>;
  //   const subscription = watch(async () => {
  //     const isFormValid = await trigger();
  //     const isFormDirty = isDirty;

  //     const shouldSave = isFormValid && isFormDirty;

  //     if (shouldSave) {
  //       clearTimeout(timeoutId);

  //       if (!isSurveyCreated) {
  //         form.handleSubmit(handleSubmit)();
  //       } else {
  //         timeoutId = setTimeout(() => {
  //           form.handleSubmit(handleSubmit)();
  //         }, 1000);
  //       }
  //     }
  //   });
  //   return () => {
  //     subscription.unsubscribe();
  //     clearTimeout(timeoutId);
  //   };
  // }, [watch, trigger, isDirty, handleSubmit, form, isSurveyCreated]);

  useEffect(() => {
    if (onSave) {
      const saveFunction = () => {
        form.handleSubmit(handleSubmit)();
      };

      (window as any).surveyFormSave = saveFunction;
    }

    if (onDelete) {
      (window as any).surveyFormDelete = onDelete;
    }

    return () => {
      delete (window as any).surveyFormSave;
      delete (window as any).surveyFormDelete;
    };
  }, [onSave, onDelete, form]);

  useEffect(() => {
    const options = form.watch('question.options');
    if (options && options.length > 0) {
      if (form.watch('question.type') === 'SINGLE_CHOICE') {
        setSelectedRadioIndex(0);
      } else {
        setSelectedCheckboxes(new Set([0]));
      }
    }
  }, [form.watch('question.options'), form.watch('question.type')]);

  return (
    <Form {...form}>
      {isModal ? (
        <div className={cn('flex flex-col gap-4', locked && 'pointer-events-none opacity-90')}>
          <div className="flex flex-col w-full">
            <div className="flex items-center gap-2 w-full">
              {canRemove && !locked && (
                <button
                  type="button"
                  onClick={onRemove}
                  className="bg-white rounded-lg mr-4 py-3 px-2 border border-input cursor-pointer"
                >
                  <Trash2 className="size-6" />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <Title level={2}>{t('surveys.panelTitle')}</Title>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                {/* question + type */}
                <div className="flex items-start gap-2 w-full flex-col lg:flex-row">
                  <FormField
                    name="question.textContent"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <FormItem className="w-full lg:w-2/3">
                        <TextFieldWithLimit
                          className="bg-white"
                          label={t('surveys.questionLabel')}
                          value={field.value || ''}
                          onChange={(v) => field.onChange(v)}
                          maxLength={300}
                          error={fieldState.error?.message}
                        />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="question.type"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="w-full lg:w-1/3">
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger label={t('surveys.questionTypeLabel')} className="h-14">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SINGLE_CHOICE">
                              {t('surveys.questionType.single')}
                            </SelectItem>
                            <SelectItem value="MULTIPLE_CHOICE">
                              {t('surveys.questionType.multiple')}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                </div>

                {/* Section Réponses */}
                <div className="flex flex-col gap-2 pt-4">
                  <div className="space-y-4">
                    {form.watch('question.options')?.map((_, answerIndex: number) => (
                      <div key={answerIndex} className="flex flex-col">
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <FormField
                              name={`question.options.${answerIndex}.textContent`}
                              control={form.control}
                              render={({ field, fieldState }) => (
                                <FormItem>
                                  <TextFieldWithLimit
                                    className="bg-white"
                                    label={t('surveys.answerLabel', {
                                      index: answerIndex + 1,
                                    })}
                                    value={(field.value as string) || ''}
                                    onChange={(v) => field.onChange(v)}
                                    maxLength={300}
                                    error={fieldState.error?.message}
                                  />
                                </FormItem>
                              )}
                            />
                          </div>

                          {form.watch('question.options')?.length > 2 && !locked && (
                            <button
                              type="button"
                              onClick={() => removeAnswer(answerIndex)}
                              className="bg-white h-14 rounded-lg px-2 border border-input cursor-pointer"
                            >
                              <Trash2 className="size-6" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {form.watch('question.options')?.length < 6 && !locked && (
                    <>
                      <button
                        type="button"
                        onClick={addAnswer}
                        className="flex items-center gap-2 pt-4"
                      >
                        <Plus className="size-6" />
                        <Label className="cursor-pointer text-sm">{t('surveys.addAnswer')}</Label>
                      </button>
                      <Separator className="bg-input" />
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-4 pt-8">
                <Title level={2}>{t('surveys.colorsTitle')}</Title>

                <div className="grid lg:grid-cols-2 gap-4 w-full min-w-0">
                  {/* background */}
                  <div className="flex items-center gap-4 relative min-w-0">
                    <input
                      type="color"
                      value={getFinalColor('backgroundColor')}
                      onChange={(e) => {
                        form.setValue('backgroundColor', 'custom');
                        form.setValue('customBackgroundColor', e.target.value);
                      }}
                      className="sr-only"
                      id="background-color-picker"
                    />
                    <label
                      htmlFor="background-color-picker"
                      className="size-14 shrink-0 rounded-lg border border-input cursor-pointer"
                      style={getBackgroundStyle(
                        form.watch('backgroundColor') || 'accent',
                        form.watch('customBackgroundColor'),
                      )}
                    />
                    <div className="w-full min-w-0">
                      <FormField
                        name="backgroundColor"
                        control={form.control}
                        render={({ field }) => (
                          <FormItem className="h-14">
                            <Select
                              value={field.value}
                              onValueChange={(value) => {
                                field.onChange(value);
                                if (value !== 'custom') {
                                  form.setValue('customBackgroundColor', '#000000');
                                }
                              }}
                            >
                              <SelectTrigger
                                label={t('surveys.backgroundColorLabel')}
                                className="h-14"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {colorsArray.map((color) => (
                                  <SelectItem key={color.id} value={color.id}>
                                    {color.name}
                                  </SelectItem>
                                ))}
                                <SelectItem value="custom">{t('surveys.customColor')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Borders */}
                  <div className="flex items-center gap-4 relative min-w-0">
                    <input
                      type="color"
                      value={getFinalColor('borderColor')}
                      onChange={(e) => {
                        form.setValue('borderColor', 'custom');
                        form.setValue('customBorderColor', e.target.value);
                      }}
                      className="sr-only"
                      id="border-color-picker"
                    />
                    <label
                      htmlFor="border-color-picker"
                      className="size-14  rounded-lg border border-input cursor-pointer"
                      style={{
                        ...getBackgroundStyle(
                          form.watch('borderColor') || 'accent',
                          form.watch('customBorderColor'),
                        ),
                        ...getBorderStyle(
                          form.watch('borderColor') || 'accent',
                          form.watch('customBorderColor'),
                        ),
                      }}
                    >
                      {form.watch('borderColor') === 'no-border' && (
                        <div
                          className="w-full h-full flex items-center justify-center rounded-lg border border-input"
                          style={{
                            backgroundColor: 'white',
                            position: 'relative',
                          }}
                        >
                          <div
                            className="w-full h-0.5 bg-black"
                            style={{
                              transform: 'rotate(135deg)',
                            }}
                          />
                        </div>
                      )}
                    </label>
                    <div className="w-full">
                      <FormField
                        name="borderColor"
                        control={form.control}
                        render={({ field }) => (
                          <FormItem className="h-14">
                            <Select
                              value={field.value}
                              onValueChange={(value) => {
                                field.onChange(value);
                                if (value !== 'custom') {
                                  form.setValue('customBorderColor', '#000000');
                                }
                              }}
                            >
                              <SelectTrigger
                                label={t('surveys.borderColorLabel')}
                                className="h-14 max-w-full w-full"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {borderColorsArray.map((color) => (
                                  <SelectItem key={color.id} value={color.id}>
                                    {color.name}
                                  </SelectItem>
                                ))}
                                <SelectItem value="custom">{t('surveys.customColor')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Title */}
                  <div className="flex items-center gap-4">
                    <div
                      className="size-14 shrink-0 rounded-lg border border-input"
                      style={{
                        backgroundColor: form.watch('titleColor') || '#000000',
                      }}
                    />

                    <div className="w-full">
                      <FormField
                        name="titleColor"
                        control={form.control}
                        render={({ field }) => (
                          <FormItem className="h-14">
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger label={t('surveys.titleColorLabel')} className="h-14">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {arrayFontColors.map((color) => (
                                  <SelectItem key={color.name} value={color.value}>
                                    {color.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* couleur d'accent*/}
                  <div className="flex items-center gap-4 relative">
                    <input
                      type="color"
                      value={getFinalColor('accentColor')}
                      onChange={(e) => {
                        form.setValue('accentColor', 'custom');
                        form.setValue('customAccentColor', e.target.value);
                      }}
                      className="sr-only"
                      id="accent-color-picker"
                    />
                    <label
                      htmlFor="accent-color-picker"
                      className="size-14 shrink-0 rounded-lg border border-input cursor-pointer"
                      style={getBackgroundStyle(
                        form.watch('accentColor') || 'accent',
                        form.watch('customAccentColor'),
                      )}
                    />
                    <div className="w-full">
                      <FormField
                        name="accentColor"
                        control={form.control}
                        render={({ field }) => (
                          <FormItem className="h-14">
                            <Select
                              value={field.value}
                              onValueChange={(value) => {
                                field.onChange(value);
                                if (value !== 'custom') {
                                  form.setValue('customAccentColor', '#000000');
                                }
                              }}
                            >
                              <SelectTrigger label={t('surveys.accentColorLabel')} className="h-14">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {colorsArray.map((color) => (
                                  <SelectItem key={color.id} value={color.id}>
                                    {color.name}
                                  </SelectItem>
                                ))}
                                <SelectItem value="custom">{t('surveys.customColor')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Accordion type="single" collapsible className="pt-8" defaultValue="preview">
                <AccordionItem value="preview">
                  <AccordionTrigger isChevron className=" rounded-[20px] py-6 px-0">
                    <Title level={2}>{t('surveys.previewTitle')}</Title>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div
                      className="max-w-[550px] w-full mx-auto rounded-lg p-4 flex flex-col gap-4 border-2 "
                      style={{
                        ...getBackgroundStyle(
                          form.watch('backgroundColor') || 'accent',
                          form.watch('customBackgroundColor'),
                        ),
                        ...getBorderStyle(
                          form.watch('borderColor') || 'accent',
                          form.watch('customBorderColor'),
                        ),
                      }}
                    >
                      <Title level={2}>
                        <span
                          style={{
                            color: form.watch('titleColor') || '#000000',
                          }}
                        >
                          {form.watch('question.textContent')}
                        </span>
                      </Title>
                      {form.watch('question.type') === 'SINGLE_CHOICE' ? (
                        <RadioGroup
                          value={String(selectedRadioIndex)}
                          onValueChange={(value) => setSelectedRadioIndex(Number(value))}
                        >
                          {form.watch('question.options')?.map((answer, answerIndex: number) => (
                            <div
                              key={answerIndex}
                              className="flex items-center space-x-2 p-2 rounded-lg bg-white border border-input"
                            >
                              <RadioGroupItem
                                value={String(answerIndex)}
                                id={`survey-${surveyIndex}-answer-${answerIndex}`}
                                style={
                                  {
                                    '--accent-color': getFinalColor('accentColor'),
                                  } as React.CSSProperties
                                }
                                className="data-[state=checked]:bg-(--accent-color) data-[state=checked]:border-(--accent-color)"
                              />
                              <Label
                                className=" cursor-pointer"
                                htmlFor={`survey-${surveyIndex}-answer-${answerIndex}`}
                              >
                                {answer?.textContent}
                              </Label>
                            </div>
                          ))}
                          <Button
                            className="w-full"
                            style={{
                              backgroundColor: getFinalColor('accentColor'),
                              color: getReadableTextColor(getFinalColor('accentColor')),
                            }}
                          >
                            {tPreview('live.survey.submit')}
                          </Button>
                        </RadioGroup>
                      ) : (
                        <div className="flex flex-col space-y-2">
                          {form.watch('question.options')?.map((answer, answerIndex: number) => (
                            <div
                              key={answerIndex}
                              className="flex items-center space-x-2 p-2 rounded-lg bg-white border border-input"
                            >
                              <Checkbox
                                id={`survey-${surveyIndex}-answer-${answerIndex}`}
                                checked={selectedCheckboxes.has(answerIndex)}
                                onCheckedChange={(checked) => {
                                  const newSet = new Set(selectedCheckboxes);
                                  if (checked) {
                                    newSet.add(answerIndex);
                                  } else {
                                    newSet.delete(answerIndex);
                                  }
                                  setSelectedCheckboxes(newSet);
                                }}
                                style={
                                  {
                                    '--accent-color': getFinalColor('accentColor'),
                                  } as React.CSSProperties
                                }
                                className="data-[state=checked]:bg-(--accent-color) data-[state=checked]:border-(--accent-color)"
                              />
                              <Label
                                className="cursor-pointer"
                                htmlFor={`survey-${surveyIndex}-answer-${answerIndex}`}
                              >
                                {answer?.textContent}
                              </Label>
                            </div>
                          ))}
                          <Button
                            className="w-full"
                            style={{
                              backgroundColor: getFinalColor('accentColor'),
                              color: getReadableTextColor(getFinalColor('accentColor')),
                            }}
                          >
                            {tPreview('live.survey.submit')}
                          </Button>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <AccordionItem
            value={`item-survey-${surveyIndex}`}
            className={`bg-medium p-6 ${isEvergreen ? 'rounded-none rounded-t-[20px] border-b-2' : ''}`}
          >
            <div className="flex flex-col w-full">
              <div className="flex items-start gap-2 w-full">
                <div className={cn('flex-1', locked && 'pointer-events-none opacity-90')}>
                  <FormField
                    name="title"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <TextFieldWithLimit
                          className="bg-white"
                          label={t('surveys.formTitle', { index: surveyIndex + 1 })}
                          value={field.value ?? ''}
                          onChange={(v) => field.onChange(v)}
                          maxLength={100}
                          error={fieldState.error?.message}
                        />
                      </FormItem>
                    )}
                  />
                </div>
                {canRemove && !locked && (
                  <button
                    type="button"
                    onClick={onRemove}
                    className="bg-white rounded-lg h-14 mr-4 py-3 px-2 border border-input cursor-pointer"
                  >
                    <Trash2 className="size-6" />
                  </button>
                )}
                <AccordionTrigger isChevron className=" rounded-[20px] p-0 h-14!" />
              </div>
            </div>
            <AccordionContent className="flex flex-col gap-4 bg-medium pt-4 pb-0 px-0">
              <div
                className={cn('flex flex-col gap-4', locked && 'pointer-events-none opacity-90')}
              >
                <Title level={2}>{t('surveys.panelTitle')}</Title>

                {/* Section Questions */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col">
                    {/* question + type */}
                    <div className="flex items-start gap-2 w-full flex-col lg:flex-row">
                      <FormField
                        name="question.textContent"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <FormItem className="w-full lg:w-2/3">
                            <TextFieldWithLimit
                              className="bg-white"
                              label={t('surveys.questionLabel')}
                              value={field.value || ''}
                              onChange={(v) => field.onChange(v)}
                              maxLength={300}
                              error={fieldState.error?.message}
                            />
                          </FormItem>
                        )}
                      />
                      <FormField
                        name="question.type"
                        control={form.control}
                        render={({ field }) => (
                          <FormItem className="w-full lg:w-1/3">
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger
                                label={t('surveys.questionTypeLabel')}
                                className="h-14"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="SINGLE_CHOICE">
                                  {t('surveys.questionType.single')}
                                </SelectItem>
                                <SelectItem value="MULTIPLE_CHOICE">
                                  {t('surveys.questionType.multiple')}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Section Réponses */}
                    <div className="flex flex-col gap-2 pt-4">
                      <div className="space-y-4">
                        {form.watch('question.options')?.map((_, answerIndex: number) => (
                          <div key={answerIndex} className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <FormField
                                  name={`question.options.${answerIndex}.textContent`}
                                  control={form.control}
                                  render={({ field, fieldState }) => (
                                    <FormItem>
                                      <TextFieldWithLimit
                                        className="bg-white"
                                        label={t('surveys.answerLabel', {
                                          index: answerIndex + 1,
                                        })}
                                        value={(field.value as string) || ''}
                                        onChange={(v) => field.onChange(v)}
                                        maxLength={300}
                                        error={fieldState.error?.message}
                                      />
                                    </FormItem>
                                  )}
                                />
                              </div>

                              {form.watch('question.options')?.length > 2 && !locked && (
                                <button
                                  type="button"
                                  onClick={() => removeAnswer(answerIndex)}
                                  className="bg-white h-12 rounded-lg px-2 border border-input cursor-pointer"
                                >
                                  <Trash2 className="size-6" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {form.watch('question.options')?.length < 6 && !locked && (
                        <>
                          <button
                            type="button"
                            onClick={addAnswer}
                            className="flex items-center gap-2 pt-4"
                          >
                            <Plus className="size-6" />
                            <Label className="cursor-pointer text-sm">
                              {t('surveys.addAnswer')}
                            </Label>
                          </button>
                          <Separator className="bg-input" />
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 pt-8">
                    <Title level={2}>{t('surveys.colorsTitle')}</Title>

                    <div className="grid lg:grid-cols-2 gap-4 flex flex-col lg:flex-row">
                      {/* background */}
                      <ColorSelector
                        form={form}
                        offerType="A"
                        colorsArray={colorsArray}
                        colorFieldName="backgroundColor"
                        customColorFieldName="customBackgroundColor"
                        label={t('surveys.backgroundColorLabel')}
                        customColorLabel={t('surveys.customColor')}
                        getFinalColor={(colorType) => getFinalColor(colorType as 'backgroundColor')}
                        getStyle={getBackgroundStyle}
                      />

                      {/* Borders */}
                      <ColorSelector
                        form={form}
                        offerType="A"
                        colorsArray={colorsArray}
                        colorFieldName="borderColor"
                        customColorFieldName="customBorderColor"
                        label={t('surveys.borderColorLabel')}
                        customColorLabel={t('surveys.customColor')}
                        noBorderLabel={t('surveys.noBorder')}
                        showNoBorder={true}
                        getFinalColor={(colorType) => getFinalColor(colorType as 'borderColor')}
                        getStyle={(colorId, customColor) => {
                          if (colorId === 'no-border') {
                            return { backgroundColor: 'white' };
                          }
                          return getBackgroundStyle(colorId, customColor);
                        }}
                      />

                      {/* Title */}
                      <ColorSelector
                        form={form}
                        offerType="A"
                        colorsArray={arrayFontColors.map((color) => ({
                          id: color.value,
                          name: color.name,
                          value: color.value,
                        }))}
                        colorFieldName="titleColor"
                        label={t('surveys.titleColorLabel')}
                        showCustomColor={false}
                        defaultValue="#000000"
                      />

                      {/* couleur d'accent*/}
                      <ColorSelector
                        form={form}
                        offerType="A"
                        colorsArray={colorsArray}
                        colorFieldName="accentColor"
                        customColorFieldName="customAccentColor"
                        label={t('surveys.accentColorLabel')}
                        customColorLabel={t('surveys.customColor')}
                        getFinalColor={(colorType) => getFinalColor(colorType as 'accentColor')}
                        getStyle={getBackgroundStyle}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 pt-8">
                    <Title level={2}>{t('surveys.previewTitle')}</Title>

                    <div
                      className="max-w-[550px] w-full mx-auto rounded-lg p-4 flex flex-col gap-4 border-2"
                      style={{
                        ...getBackgroundStyle(
                          form.watch('backgroundColor') || 'accent',
                          form.watch('customBackgroundColor'),
                        ),
                        ...getBorderStyle(
                          form.watch('borderColor') || 'accent',
                          form.watch('customBorderColor'),
                        ),
                      }}
                    >
                      <Title level={2}>
                        <span
                          style={{
                            color: form.watch('titleColor') || '#000000',
                          }}
                        >
                          {form.watch('question.textContent')}
                        </span>
                      </Title>
                      {form.watch('question.type') === 'SINGLE_CHOICE' ? (
                        <RadioGroup
                          value={String(selectedRadioIndex)}
                          onValueChange={(value) => setSelectedRadioIndex(Number(value))}
                        >
                          {form.watch('question.options')?.map((answer, answerIndex: number) => (
                            <div
                              key={answerIndex}
                              className="flex items-center space-x-2 p-2 rounded-lg bg-white border border-input"
                            >
                              <RadioGroupItem
                                value={String(answerIndex)}
                                id={`${answerIndex}`}
                                style={
                                  {
                                    '--accent-color': getFinalColor('accentColor'),
                                  } as React.CSSProperties
                                }
                                className="data-[state=checked]:bg-(--accent-color) data-[state=checked]:border-(--accent-color)"
                              />
                              <Label htmlFor={`${answerIndex}`}>{answer?.textContent}</Label>
                            </div>
                          ))}
                          <Button
                            className="w-full"
                            style={{
                              backgroundColor: getFinalColor('accentColor'),
                              color: getReadableTextColor(getFinalColor('accentColor')),
                            }}
                          >
                            {tPreview('live.survey.submit')}
                          </Button>
                        </RadioGroup>
                      ) : (
                        <div className="flex flex-col space-y-2">
                          {form.watch('question.options')?.map((answer, answerIndex: number) => (
                            <div
                              key={answerIndex}
                              className="flex items-center space-x-2 p-2 rounded-lg bg-white border border-input"
                            >
                              <Checkbox
                                id={`${answerIndex}`}
                                checked={selectedCheckboxes.has(answerIndex)}
                                onCheckedChange={(checked) => {
                                  const newSet = new Set(selectedCheckboxes);
                                  if (checked) {
                                    newSet.add(answerIndex);
                                  } else {
                                    newSet.delete(answerIndex);
                                  }
                                  setSelectedCheckboxes(newSet);
                                }}
                                style={
                                  {
                                    '--accent-color': getFinalColor('accentColor'),
                                  } as React.CSSProperties
                                }
                                className="data-[state=checked]:bg-(--accent-color) data-[state=checked]:border-(--accent-color)"
                              />
                              <Label htmlFor={`${answerIndex}`}>{answer?.textContent}</Label>
                            </div>
                          ))}
                          <Button
                            className="w-full"
                            style={{
                              backgroundColor: getFinalColor('accentColor'),
                              color: getReadableTextColor(getFinalColor('accentColor')),
                            }}
                          >
                            {tPreview('live.survey.submit')}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {!batchMode && (
                  <Button
                    type="button"
                    onClick={form.handleSubmit(handleSubmit)}
                    className="mt-4 disabled:bg-gray-200 disabled:text-gray-500"
                    disabled={locked || isSubmitting || !form.formState.isValid}
                  >
                    {isSubmitting && <Loader />}
                    {t('save')}
                  </Button>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </div>
      )}
    </Form>
  );
});

export default SurveyForm;
